import { type SubmitEvent, useRef, useState } from 'react';
import { Box, Button, Link, Stack, TextField, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { ErrorCode, StartRegistrationRequestSchema, WEB_ROUTES } from '@lsc/shared';
import type { z } from 'zod';
import { apiRequest } from '../../api/client';
import { type ApiError, isApiError } from '../../api/errors';
import { useRuntimeConfig } from '../../config/runtime-config';
import { ActionError } from '../components/ActionError';
import { ErrorState } from '../components/ErrorState';
import { PageHeading } from '../components/PageHeading';
import { Turnstile, type TurnstileHandle } from '../../components/Turnstile';
import { VisuallyHidden } from '../components/VisuallyHidden';
import { publicQueryKeys } from '../api';

const FormSchema = StartRegistrationRequestSchema.omit({ turnstileToken: true });
type FormValues = z.input<typeof FormSchema>;
type FieldName = keyof FormValues;
type FieldErrors = Partial<Record<FieldName, string>>;

const FIELD_ORDER: readonly FieldName[] = ['firstName', 'lastName', 'email'];
const EMPTY_VALUES: FormValues = { firstName: '', lastName: '', email: '' };

/** Errors after which the code itself cannot be used – the form is replaced by an error page. */
const CODE_ERRORS: ReadonlySet<string> = new Set([
  ErrorCode.ACTIVATION_CODE_INVALID,
  ErrorCode.ACTIVATION_CODE_USED,
  ErrorCode.ACTIVATION_CODE_DISABLED,
  ErrorCode.ACTIVATION_CODE_EXPIRED,
  ErrorCode.BATCH_DISABLED,
  ErrorCode.ACTIVATION_ALREADY_PENDING,
]);

function isFieldName(path: string): path is FieldName {
  return (FIELD_ORDER as readonly string[]).includes(path);
}

function validate(values: FormValues): FieldErrors {
  const result = FormSchema.safeParse(values);
  const errors: FieldErrors = {};
  if (result.success) return errors;
  for (const issue of result.error.issues) {
    const field = String(issue.path[0]);
    if (isFieldName(field)) errors[field] ??= issue.message;
  }
  return errors;
}

function serverFieldErrors(error: ApiError): FieldErrors {
  const errors: FieldErrors = {};
  for (const { path, message } of error.fieldErrors) {
    if (isFieldName(path)) errors[path] ??= message;
  }
  return errors;
}

function focusFirstInvalid(errors: FieldErrors) {
  const first = FIELD_ORDER.find((field) => errors[field] !== undefined);
  if (first) document.getElementById(first)?.focus();
}

export interface ActivationFormProps {
  /** Formatted code `XXXXX-XXXXX` as echoed by the server. */
  code: string;
  companyName: string | null;
}

/** Activation form (§50): names, email, privacy notice (§63), Turnstile (§145). */
export function ActivationForm({ code, companyName }: ActivationFormProps) {
  const config = useRuntimeConfig();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const turnstileRef = useRef<TurnstileHandle>(null);
  const [values, setValues] = useState<FormValues>(EMPTY_VALUES);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileMissing, setTurnstileMissing] = useState(false);

  const mutation = useMutation({
    mutationFn: (body: FormValues & { turnstileToken: string }) =>
      apiRequest('startRegistration', { params: { code }, body }),
    onSuccess: (data) => {
      queryClient.setQueryData(publicQueryKeys.registrationStatus, data);
      void navigate(WEB_ROUTES.registrationStatus);
    },
    onError: (error) => {
      // Turnstile tokens are single-use.
      turnstileRef.current?.reset();
      if (isApiError(error) && error.code === ErrorCode.VALIDATION_FAILED) {
        const errors = serverFieldErrors(error);
        setFieldErrors(errors);
        focusFirstInvalid(errors);
      }
    },
  });

  if (mutation.error && isApiError(mutation.error) && CODE_ERRORS.has(mutation.error.code)) {
    return <ErrorState error={mutation.error} />;
  }

  function handleChange(field: FieldName, value: string) {
    setValues((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field] !== undefined) {
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  }

  function handleTurnstileToken(token: string | null) {
    setTurnstileToken(token);
    if (token !== null) setTurnstileMissing(false);
  }

  function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    const errors = validate(values);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      focusFirstInvalid(errors);
      return;
    }
    if (turnstileToken === null) {
      setTurnstileMissing(true);
      return;
    }
    mutation.mutate({ ...values, turnstileToken });
  }

  const showActionError =
    mutation.error !== null &&
    !(isApiError(mutation.error) && mutation.error.code === ErrorCode.VALIDATION_FAILED);

  return (
    <Stack spacing={3}>
      <Box>
        <PageHeading>Willkommen</PageHeading>
        {companyName ? (
          <Typography>
            Sie aktivieren Ihren Zugang für:{' '}
            <Box component="strong" sx={{ display: 'block', fontSize: '1.125rem', mt: 0.5 }}>
              {companyName}
            </Box>
          </Typography>
        ) : (
          <Typography>Bitte geben Sie Ihre Daten ein, um Ihren Zugang anzufordern.</Typography>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Aktivierungscode: <span translate="no">{code}</span>
        </Typography>
      </Box>

      <Box component="form" noValidate onSubmit={handleSubmit} aria-label="Zugang anfordern">
        <Stack spacing={2.5}>
          <TextField
            id="firstName"
            label="Vorname"
            required
            value={values.firstName}
            onChange={(e) => {
              handleChange('firstName', e.target.value);
            }}
            error={fieldErrors.firstName !== undefined}
            helperText={fieldErrors.firstName}
            autoComplete="given-name"
          />
          <TextField
            id="lastName"
            label="Nachname"
            required
            value={values.lastName}
            onChange={(e) => {
              handleChange('lastName', e.target.value);
            }}
            error={fieldErrors.lastName !== undefined}
            helperText={fieldErrors.lastName}
            autoComplete="family-name"
          />
          <TextField
            id="email"
            label="E-Mail-Adresse"
            type="email"
            required
            value={values.email}
            onChange={(e) => {
              handleChange('email', e.target.value);
            }}
            error={fieldErrors.email !== undefined}
            helperText={
              fieldErrors.email ??
              'Bitte verwenden Sie Ihre private E-Mail-Adresse. Sie erhalten dorthin einen Bestätigungslink.'
            }
            autoComplete="email"
            slotProps={{
              htmlInput: { inputMode: 'email', autoCapitalize: 'none', spellCheck: false },
            }}
          />

          <Typography variant="body2" color="text.secondary">
            Wir verwenden Ihre Angaben ausschließlich, um Ihren Zugang zur Lernplattform
            einzurichten. Weitere Informationen finden Sie in unserer{' '}
            <Link href={config.privacyPolicyUrl} target="_blank" rel="noopener noreferrer">
              Datenschutzerklärung
              <VisuallyHidden> (öffnet in neuem Tab)</VisuallyHidden>
            </Link>
            .
          </Typography>

          <Box>
            <Turnstile
              ref={turnstileRef}
              action="registration-start"
              onToken={handleTurnstileToken}
            />
            {turnstileMissing ? (
              <Typography role="alert" color="error" variant="body2">
                Bitte bestätigen Sie die Sicherheitsprüfung.
              </Typography>
            ) : null}
          </Box>

          {showActionError ? <ActionError error={mutation.error} /> : null}

          <Button type="submit" variant="contained" size="large" loading={mutation.isPending}>
            Zugang anfordern
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
}
